import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth, API, usePush } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  PlayCircle, 
  FileText, 
  Download,
  Clock,
  User,
  ArrowLeft,
  Calendar,
  CheckCircle,
  Send,
  Upload,
  Award,
  Trophy,
  Check,
  File,
  X,
  Loader2,
  ExternalLink,
  Copy,
  RefreshCw
} from "lucide-react";

const CourseDetail = () => {
  const { courseId } = useParams();
  const { user } = useAuth();
  const pushNotifications = usePush();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);
  const [submissionContent, setSubmissionContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [enrollmentProgress, setEnrollmentProgress] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [courseProgress, setCourseProgress] = useState(null);
  const [materialProgress, setMaterialProgress] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const [certificateRequest, setCertificateRequest] = useState(null);
  const [requestingCertificate, setRequestingCertificate] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState(null);
  const [externalLink, setExternalLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);

  // Generate external link for watching video outside app
  const handleGetExternalLink = async () => {
    if (!activeVideo?.material_id) return;
    
    setGeneratingLink(true);
    try {
      const response = await axios.get(
        `${API}/materials/${activeVideo.material_id}/external-link`,
        { withCredentials: true }
      );
      setExternalLink(response.data.external_url);
      toast.success('External link generated! Valid for 24 hours.');
    } catch (error) {
      console.error('Failed to generate external link:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate link');
    } finally {
      setGeneratingLink(false);
    }
  };

  // Copy link to clipboard
  const handleCopyLink = () => {
    if (externalLink) {
      navigator.clipboard.writeText(externalLink);
      toast.success('Link copied to clipboard!');
    }
  };

  // Retry video loading
  const handleRetryVideo = () => {
    setVideoError(null);
    setVideoUrl(null);
    // Trigger re-load by changing activeVideo reference
    if (activeVideo) {
      const temp = { ...activeVideo };
      setActiveVideo(null);
      setTimeout(() => setActiveVideo(temp), 100);
    }
  };

  useEffect(() => {
    fetchCourseData();
  }, [courseId]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  // Load video when activeVideo changes
  useEffect(() => {
    const loadVideo = async () => {
      if (!activeVideo) {
        setVideoUrl(null);
        setVideoError(null);
        setExternalLink(null);
        return;
      }

      // If it's an external URL (YouTube, Vimeo, etc.), use directly
      if (activeVideo.url && !activeVideo.url.startsWith('/api/')) {
        setVideoUrl(activeVideo.url);
        setVideoError(null);
        return;
      }

      // If it's an uploaded video, get direct URL to processed file
      if (activeVideo.url && (activeVideo.is_uploaded_video || activeVideo.url.includes('/stream') || activeVideo.url.includes('/download'))) {
        setVideoLoading(true);
        setVideoError(null);
        setExternalLink(null);
        
        try {
          // Get direct URL to the processed video file
          const directUrlResponse = await axios.get(
            `${API}/materials/${activeVideo.material_id}/direct-url`,
            { withCredentials: true }
          );
          
          const data = directUrlResponse.data;
          
          if (!data.ready) {
            // Video is not ready - show appropriate message
            if (data.status === 'pending') {
              setVideoError('Video is queued for processing. Please check back in a few minutes.');
            } else if (data.status === 'processing') {
              setVideoError('Video is being processed. This may take a few minutes. Please refresh to check status.');
            } else if (data.status === 'failed') {
              setVideoError(`Video processing failed: ${data.message}. Please contact your administrator.`);
            } else {
              setVideoError(data.message || 'Video is not available.');
            }
            setVideoUrl(null);
            setVideoLoading(false);
            return;
          }
          
          // Video is ready - use the direct URL
          let videoUrl = data.direct_url;
          
          // If it's a relative URL, make it absolute
          if (videoUrl.startsWith('/')) {
            videoUrl = `${API.replace('/api', '')}${videoUrl}`;
          }
          
          console.log('Direct video URL:', videoUrl);
          setVideoUrl(videoUrl);
          
        } catch (error) {
          console.error('Failed to get video URL:', error);
          const errorDetail = error.response?.data?.detail || 'Failed to load video.';
          setVideoError(errorDetail);
          setVideoUrl(null);
        } finally {
          setVideoLoading(false);
        }
      } else {
        setVideoUrl(activeVideo.url);
      }
    };

    loadVideo();
  }, [activeVideo]);

  const fetchCourseData = async () => {
    try {
      const [courseRes, submissionsRes, progressRes, courseProgressRes] = await Promise.all([
        axios.get(`${API}/courses/${courseId}`, { withCredentials: true }),
        axios.get(`${API}/submissions`, { withCredentials: true }),
        axios.get(`${API}/enrollments/${courseId}/progress`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/progress/course/${courseId}`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      setCourse(courseRes.data);
      setSubmissions(submissionsRes.data);
      setEnrollmentProgress(progressRes.data);
      
      if (courseProgressRes.data) {
        setCourseProgress(courseProgressRes.data);
        // Build progress map for materials
        const progMap = {};
        courseProgressRes.data.materials?.forEach(m => {
          progMap[m.material_id] = {
            progress: m.view_progress || 0,
            is_completed: m.is_completed || false
          };
        });
        setMaterialProgress(progMap);
      }
      
      // Set first video as active if exists
      const videos = courseRes.data.materials?.filter(m => m.type === 'video') || [];
      if (videos.length > 0) {
        setActiveVideo(videos[0]);
      }
    } catch (error) {
      toast.error("Failed to load course");
    } finally {
      setLoading(false);
    }
  };

  // Track video progress
  const updateVideoProgress = async (materialId, progress, watchTime) => {
    try {
      const response = await axios.post(`${API}/progress/material/${materialId}`, {
        progress: Math.round(progress),
        watch_time: Math.round(watchTime)
      }, { withCredentials: true });
      
      // Update local state
      setMaterialProgress(prev => ({
        ...prev,
        [materialId]: {
          progress: response.data.progress,
          is_completed: response.data.is_completed
        }
      }));
      
      // If completed, check if all materials are done for auto-completion
      if (response.data.is_completed) {
        checkForAutoCompletion();
      }
    } catch (error) {
      console.error("Failed to update progress:", error);
    }
  };

  // Start tracking video progress
  const startProgressTracking = (video) => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    
    // Track progress every 10 seconds
    progressIntervalRef.current = setInterval(() => {
      const iframe = document.querySelector('iframe');
      if (iframe && video) {
        // For YouTube videos, we estimate progress based on time
        // In a real implementation, you'd use the YouTube API
        const currentProgress = materialProgress[video.material_id]?.progress || 0;
        if (currentProgress < 100) {
          updateVideoProgress(video.material_id, Math.min(currentProgress + 5, 100), 10);
        }
      }
    }, 10000);
  };

  // Stop tracking when video changes
  useEffect(() => {
    if (activeVideo) {
      startProgressTracking(activeVideo);
    }
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [activeVideo]);

  // Check if all materials completed for auto-completion
  const checkForAutoCompletion = async () => {
    try {
      const progressRes = await axios.get(`${API}/progress/course/${courseId}`, { withCredentials: true });
      const allCompleted = progressRes.data.materials?.every(m => m.is_completed);
      
      if (allCompleted && !progressRes.data.is_completed) {
        toast.success("All materials completed! You can now claim your certificate.", {
          duration: 5000
        });
      }
      
      setCourseProgress(progressRes.data);
    } catch (error) {
      console.error("Failed to check auto-completion:", error);
    }
  };

  // Mark video as complete manually
  const handleMarkVideoComplete = async (materialId) => {
    await updateVideoProgress(materialId, 100, 0);
    toast.success("Marked as complete!");
    
    // Track lesson completion for push notification eligibility
    if (pushNotifications?.trackEngagement) {
      pushNotifications.trackEngagement('lesson_completed');
    }
  };

  // Handle video progress during playback
  const handleVideoProgress = async (materialId, progress) => {
    // Debounce progress updates - only update if significantly changed
    const currentProgress = materialProgress[materialId]?.progress || 0;
    if (progress > currentProgress && progress - currentProgress >= 5) {
      await updateVideoProgress(materialId, progress, 0);
    }
  };

  const handleRequestCertificate = async () => {
    setRequestingCertificate(true);
    try {
      const response = await axios.post(`${API}/courses/${courseId}/request-certificate`, {}, { withCredentials: true });
      toast.success("Certificate request submitted! An admin will review your request.");
      setCertificateRequest(response.data.request || response.data.certificate);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request certificate");
    } finally {
      setRequestingCertificate(false);
    }
  };

  const handleCompleteCourse = async () => {
    // Redirect to certificate request flow
    handleRequestCertificate();
  };

  const handleSubmitAssignment = async (assignmentId) => {
    if (!submissionContent && !selectedFile) {
      toast.error("Please enter some content or upload a file");
      return;
    }
    
    setSubmitting(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('assignment_id', assignmentId);
      formData.append('content', submissionContent);
      
      if (selectedFile) {
        formData.append('file', selectedFile);
      }
      
      await axios.post(`${API}/submissions`, formData, { 
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      toast.success("Assignment submitted successfully!");
      setSubmissionContent("");
      setSelectedFile(null);
      setUploadProgress(0);
      fetchCourseData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit assignment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (50MB limit for submissions)
      if (file.size > 50 * 1024 * 1024) {
        toast.error("File too large. Maximum size is 50MB");
        return;
      }
      setSelectedFile(file);
    }
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const hasSubmitted = (assignmentId) => {
    return submissions.some(s => s.assignment_id === assignmentId);
  };

  const getSubmission = (assignmentId) => {
    return submissions.find(s => s.assignment_id === assignmentId);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const videos = course?.materials?.filter(m => m.type === 'video') || [];
  const documents = course?.materials?.filter(m => m.type !== 'video') || [];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700"></div>
        </div>
      </Layout>
    );
  }

  if (!course) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-stone-900">Course not found</h2>
          <Link to="/courses">
            <Button className="mt-4">Back to Courses</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in" data-testid="course-detail">
        {/* Back Button */}
        <Link to="/courses" className="inline-flex items-center gap-2 text-stone-600 hover:text-violet-700 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to Courses
        </Link>

        {/* Course Header */}
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-1">
            <Badge variant="secondary" className="mb-3 bg-violet-100 text-violet-700">
              {course.category || 'General'}
            </Badge>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-stone-900 mb-4" style={{ fontFamily: 'Outfit' }}>
              {course.title}
            </h1>
            <p className="text-stone-600 mb-4">{course.description}</p>
            <div className="flex items-center gap-4 text-sm text-stone-500">
              <div className="flex items-center gap-1">
                <User className="h-4 w-4" />
                {course.instructor}
              </div>
              {course.duration && (
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {course.duration}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Course Completion Card */}
        {enrollmentProgress && (
          <Card className={`rounded-xl border-2 ${
            enrollmentProgress.is_completed 
              ? 'bg-emerald-50 border-emerald-200' 
              : certificateRequest?.status === 'pending'
              ? 'bg-orange-50 border-orange-200'
              : 'bg-white border-stone-100'
          }`} data-testid="completion-card">
            <CardContent className="p-6">
              {enrollmentProgress.is_completed ? (
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center">
                      <Trophy className="h-7 w-7 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-emerald-800" style={{ fontFamily: 'Outfit' }}>
                        Course Completed! 🎉
                      </h3>
                      <p className="text-emerald-600 text-sm">
                        You've earned a certificate for this course
                      </p>
                    </div>
                  </div>
                  <Link to={`/certificate/${enrollmentProgress.certificate?.certificate_id}`}>
                    <Button className="bg-emerald-600 hover:bg-emerald-700 rounded-full" data-testid="view-certificate-btn">
                      <Award className="h-4 w-4 mr-2" />
                      View Certificate
                    </Button>
                  </Link>
                </div>
              ) : certificateRequest?.status === 'pending' ? (
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center">
                      <Clock className="h-7 w-7 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-orange-800" style={{ fontFamily: 'Outfit' }}>
                        Certificate Request Pending
                      </h3>
                      <p className="text-orange-600 text-sm">
                        Your request is being reviewed by an administrator
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-orange-100 text-orange-700">
                    Awaiting Approval
                  </Badge>
                </div>
              ) : (
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-14 h-14 bg-violet-100 rounded-full flex items-center justify-center">
                      <Award className="h-7 w-7 text-violet-700" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Outfit' }}>
                        Ready to get your certificate?
                      </h3>
                      <p className="text-stone-500 text-sm">
                        Complete all course videos to request your certificate
                      </p>
                    </div>
                  </div>
                  <Button 
                    onClick={handleRequestCertificate}
                    disabled={requestingCertificate}
                    className="bg-violet-700 hover:bg-violet-800 rounded-full px-6"
                    data-testid="request-certificate-btn"
                  >
                    {requestingCertificate ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Award className="h-4 w-4 mr-2" />
                        Request Certificate
                      </>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Main Content */}
        <Tabs defaultValue="videos" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="videos" data-testid="videos-tab">
              <PlayCircle className="h-4 w-4 mr-2" />
              Lectures ({videos.length})
            </TabsTrigger>
            <TabsTrigger value="materials" data-testid="materials-tab">
              <FileText className="h-4 w-4 mr-2" />
              Materials ({documents.length})
            </TabsTrigger>
            <TabsTrigger value="assignments" data-testid="assignments-tab">
              <Calendar className="h-4 w-4 mr-2" />
              Assignments ({course.assignments?.length || 0})
            </TabsTrigger>
          </TabsList>

          {/* Videos Tab */}
          <TabsContent value="videos">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Video Player */}
              <div className="lg:col-span-2">
                {activeVideo ? (
                  <Card className="bg-white rounded-xl border border-stone-100 overflow-hidden">
                    <div className="aspect-video bg-stone-900 flex items-center justify-center">
                      {videoLoading ? (
                        <div className="text-center text-white">
                          <Loader2 className="h-16 w-16 mx-auto mb-4 animate-spin" />
                          <p>Loading video...</p>
                        </div>
                      ) : videoError ? (
                        <div className="text-center text-white p-8 max-w-md mx-auto">
                          <X className="h-12 w-12 mx-auto mb-4 text-red-400" />
                          <p className="text-red-300 mb-2 font-medium">Video Playback Issue</p>
                          <p className="text-sm text-stone-400 mb-6">{videoError}</p>
                          
                          <div className="flex flex-col gap-3">
                            <Button 
                              onClick={handleRetryVideo}
                              variant="outline"
                              className="bg-white/10 border-white/20 hover:bg-white/20"
                            >
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Retry Loading
                            </Button>
                            
                            {activeVideo?.is_uploaded_video && (
                              <Button 
                                onClick={handleGetExternalLink}
                                disabled={generatingLink}
                                className="bg-violet-600 hover:bg-violet-700"
                              >
                                {generatingLink ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                  <ExternalLink className="h-4 w-4 mr-2" />
                                )}
                                Watch in New Tab
                              </Button>
                            )}
                            
                            {externalLink && (
                              <div className="mt-4 p-3 bg-white/10 rounded-lg">
                                <p className="text-xs text-stone-300 mb-2">External Link (24hr):</p>
                                <div className="flex gap-2">
                                  <input 
                                    type="text" 
                                    value={externalLink} 
                                    readOnly 
                                    className="flex-1 text-xs bg-white/5 border border-white/20 rounded px-2 py-1"
                                  />
                                  <Button size="sm" variant="ghost" onClick={handleCopyLink}>
                                    <Copy className="h-3 w-3" />
                                  </Button>
                                </div>
                                <a 
                                  href={externalLink} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs text-violet-300 hover:text-violet-200 mt-2 inline-block"
                                >
                                  Open Link →
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : videoUrl ? (
                        videoUrl.includes('/videos/') || videoUrl.includes('/stream') || videoUrl.includes('token=') || videoUrl.includes('.mp4') || (activeVideo.is_uploaded_video || activeVideo.url?.includes('/stream') || activeVideo.url?.includes('/download')) ? (
                          // Uploaded video file - use HTML5 video player
                          <video
                            ref={videoRef}
                            src={videoUrl}
                            className="w-full h-full"
                            controls
                            controlsList="nodownload"
                            playsInline
                            preload="auto"
                            onTimeUpdate={(e) => {
                              const video = e.target;
                              if (video.duration) {
                                const progress = Math.round((video.currentTime / video.duration) * 100);
                                if (progress > 0 && progress % 10 === 0) {
                                  handleVideoProgress(activeVideo.material_id, progress);
                                }
                              }
                            }}
                            onEnded={() => handleMarkVideoComplete(activeVideo.material_id)}
                            onError={(e) => {
                              const video = e.target;
                              const errorCode = video.error?.code;
                              const errorMessage = video.error?.message;
                              console.error('Video playback error:', e);
                              console.error('Video error code:', errorCode, 'message:', errorMessage);
                              console.error('MediaError codes: 1=ABORTED, 2=NETWORK, 3=DECODE, 4=SRC_NOT_SUPPORTED');
                              console.error('Video src:', video.src);
                              console.error('Video readyState:', video.readyState);
                              console.error('Video networkState:', video.networkState);
                              
                              let errorMsg = 'Video playback failed.';
                              if (errorCode === 4) {
                                errorMsg = 'Video format not supported by browser.';
                              } else if (errorCode === 3) {
                                errorMsg = 'Video file is corrupted or cannot be decoded.';
                              } else if (errorCode === 2) {
                                errorMsg = 'Network error while loading video.';
                              } else if (errorCode === 1) {
                                errorMsg = 'Video loading was aborted.';
                              }
                              setVideoError(errorMsg);
                            }}
                          >
                            <source src={videoUrl} type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                        ) : videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be') ? (
                          // YouTube video - convert to embed URL
                          <iframe
                            src={videoUrl.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                            className="w-full h-full"
                            allowFullScreen
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            title={activeVideo.title}
                          />
                        ) : videoUrl.includes('vimeo.com') ? (
                          // Vimeo video
                          <iframe
                            src={videoUrl.replace('vimeo.com/', 'player.vimeo.com/video/')}
                            className="w-full h-full"
                            allowFullScreen
                            allow="autoplay; fullscreen; picture-in-picture"
                            title={activeVideo.title}
                          />
                        ) : (
                          // Other embed URLs
                          <iframe
                            src={videoUrl}
                            className="w-full h-full"
                            allowFullScreen
                            title={activeVideo.title}
                          />
                        )
                      ) : (
                        <div className="text-center text-white">
                          <PlayCircle className="h-16 w-16 mx-auto mb-4 opacity-50" />
                          <p>Video not available</p>
                        </div>
                      )}
                    </div>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                            {activeVideo.title}
                          </h3>
                          {activeVideo.description && (
                            <p className="text-stone-600">{activeVideo.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {/* External viewing option for uploaded videos */}
                          {activeVideo.is_uploaded_video && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleGetExternalLink}
                              disabled={generatingLink}
                              title="Get shareable link to watch outside app"
                            >
                              {generatingLink ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <ExternalLink className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          
                          {!materialProgress[activeVideo.material_id]?.is_completed && (
                            <Button
                              variant="outline"
                              onClick={() => handleMarkVideoComplete(activeVideo.material_id)}
                              data-testid="mark-complete-btn"
                            >
                              <Check className="h-4 w-4 mr-2" />
                              Mark Complete
                            </Button>
                          )}
                        </div>
                        {materialProgress[activeVideo.material_id]?.is_completed && (
                          <Badge className="bg-emerald-100 text-emerald-700 shrink-0">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Completed
                          </Badge>
                        )}
                      </div>
                      
                      {/* Progress indicator */}
                      {materialProgress[activeVideo.material_id] && !materialProgress[activeVideo.material_id].is_completed && (
                        <div className="mt-4 pt-4 border-t border-stone-100">
                          <div className="flex items-center justify-between text-sm text-stone-500 mb-2">
                            <span>Watch Progress</span>
                            <span>{materialProgress[activeVideo.material_id].progress}%</span>
                          </div>
                          <Progress value={materialProgress[activeVideo.material_id].progress} className="h-2" />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="bg-white rounded-xl border border-stone-100">
                    <CardContent className="p-12 text-center">
                      <PlayCircle className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                      <h3 className="text-xl font-medium text-stone-900 mb-2">No lectures available</h3>
                      <p className="text-stone-500">Lectures will appear here once uploaded</p>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Video List */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-stone-900" style={{ fontFamily: 'Outfit' }}>
                    Course Lectures
                  </h4>
                  {courseProgress && (
                    <Badge variant="secondary" className="bg-violet-100 text-violet-700">
                      {courseProgress.overall_progress || 0}% complete
                    </Badge>
                  )}
                </div>
                
                {/* Overall Progress Bar */}
                {courseProgress && (
                  <div className="mb-4">
                    <Progress value={courseProgress.overall_progress || 0} className="h-2" />
                  </div>
                )}
                
                {videos.length === 0 ? (
                  <p className="text-sm text-stone-500">No lectures yet</p>
                ) : (
                  videos.map((video, index) => {
                    const progress = materialProgress[video.material_id] || { progress: 0, is_completed: false };
                    
                    return (
                      <Card 
                        key={video.material_id}
                        className={`cursor-pointer transition-all ${
                          activeVideo?.material_id === video.material_id
                            ? 'ring-2 ring-violet-700 bg-violet-50'
                            : 'hover:bg-stone-50'
                        }`}
                        onClick={() => setActiveVideo(video)}
                        data-testid={`video-item-${video.material_id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              progress.is_completed
                                ? 'bg-emerald-500 text-white'
                                : activeVideo?.material_id === video.material_id
                                  ? 'bg-violet-700 text-white'
                                  : 'bg-stone-100 text-stone-600'
                            }`}>
                              {progress.is_completed ? (
                                <Check className="h-5 w-5" />
                              ) : (
                                index + 1
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-stone-900 text-sm truncate">
                                {video.title}
                              </p>
                              {progress.progress > 0 && !progress.is_completed && (
                                <div className="flex items-center gap-2 mt-1">
                                  <Progress value={progress.progress} className="h-1 flex-1" />
                                  <span className="text-xs text-stone-500">{progress.progress}%</span>
                                </div>
                              )}
                            </div>
                            {progress.is_completed ? (
                              <CheckCircle className="h-5 w-5 text-emerald-500" />
                            ) : (
                              <PlayCircle className="h-5 w-5 text-stone-400" />
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            </div>
          </TabsContent>

          {/* Materials Tab */}
          <TabsContent value="materials">
            {documents.length === 0 ? (
              <Card className="bg-white rounded-xl border border-stone-100">
                <CardContent className="p-12 text-center">
                  <FileText className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-stone-900 mb-2">No materials available</h3>
                  <p className="text-stone-500">Learning materials will appear here once uploaded</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {documents.map((doc) => (
                  <Card 
                    key={doc.material_id}
                    className="bg-white rounded-xl border border-stone-100 hover:shadow-md transition-shadow"
                    data-testid={`material-${doc.material_id}`}
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                        <FileText className="h-6 w-6 text-orange-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-stone-900 truncate">{doc.title}</p>
                        <p className="text-sm text-stone-500 capitalize">{doc.type}</p>
                      </div>
                      {(doc.url || doc.file_path) && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => window.open(doc.url || `${API}/materials/${doc.material_id}/download`, '_blank')}
                        >
                          <Download className="h-5 w-5" />
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Assignments Tab */}
          <TabsContent value="assignments">
            {!course.assignments || course.assignments.length === 0 ? (
              <Card className="bg-white rounded-xl border border-stone-100">
                <CardContent className="p-12 text-center">
                  <Calendar className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-stone-900 mb-2">No assignments yet</h3>
                  <p className="text-stone-500">Assignments will appear here when available</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {course.assignments.map((assignment) => {
                  const submitted = hasSubmitted(assignment.assignment_id);
                  const submission = getSubmission(assignment.assignment_id);
                  const isPastDue = new Date(assignment.due_date) < new Date();
                  
                  return (
                    <Card 
                      key={assignment.assignment_id}
                      className="bg-white rounded-xl border border-stone-100"
                      data-testid={`assignment-card-${assignment.assignment_id}`}
                    >
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Outfit' }}>
                                {assignment.title}
                              </h3>
                              {submitted && (
                                <Badge className="bg-emerald-100 text-emerald-700">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Submitted
                                </Badge>
                              )}
                              {!submitted && isPastDue && (
                                <Badge className="bg-red-100 text-red-700">Overdue</Badge>
                              )}
                            </div>
                            <p className="text-stone-600 mb-3">{assignment.description}</p>
                            <div className="flex items-center gap-4 text-sm">
                              <div className={`flex items-center gap-1 ${isPastDue ? 'text-red-600' : 'text-stone-500'}`}>
                                <Calendar className="h-4 w-4" />
                                Due: {formatDate(assignment.due_date)}
                              </div>
                              <div className="text-stone-500">
                                {assignment.total_marks} marks
                              </div>
                            </div>
                            {submission?.grade !== null && submission?.grade !== undefined && (
                              <div className="mt-3 p-3 bg-emerald-50 rounded-lg">
                                <p className="text-sm font-medium text-emerald-700">
                                  Grade: {submission.grade}/{assignment.total_marks}
                                </p>
                                {submission.feedback && (
                                  <p className="text-sm text-stone-600 mt-1">
                                    Feedback: {submission.feedback}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                          
                          {!submitted && !isPastDue && (
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button className="bg-violet-700 hover:bg-violet-800 rounded-xl" data-testid={`submit-assignment-${assignment.assignment_id}`}>
                                  <Send className="h-4 w-4 mr-2" />
                                  Submit
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="sm:max-w-lg">
                                <DialogHeader>
                                  <DialogTitle style={{ fontFamily: 'Outfit' }}>
                                    Submit: {assignment.title}
                                  </DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 pt-4">
                                  <div>
                                    <Label htmlFor="submission-text" className="text-sm font-medium text-stone-700">
                                      Written Submission
                                    </Label>
                                    <Textarea
                                      id="submission-text"
                                      placeholder="Enter your submission here..."
                                      value={submissionContent}
                                      onChange={(e) => setSubmissionContent(e.target.value)}
                                      rows={6}
                                      className="mt-1"
                                      data-testid="submission-textarea"
                                    />
                                  </div>
                                  
                                  <div>
                                    <Label htmlFor="submission-file" className="text-sm font-medium text-stone-700">
                                      File Upload (Optional)
                                    </Label>
                                    <div className="mt-1">
                                      <input
                                        type="file"
                                        id="submission-file"
                                        ref={fileInputRef}
                                        onChange={handleFileSelect}
                                        className="hidden"
                                        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.zip"
                                      />
                                      {!selectedFile ? (
                                        <Button
                                          type="button"
                                          variant="outline"
                                          onClick={() => fileInputRef.current?.click()}
                                          className="w-full border-dashed border-2 h-20 flex flex-col items-center justify-center gap-2"
                                        >
                                          <Upload className="h-5 w-5 text-stone-400" />
                                          <span className="text-sm text-stone-500">
                                            Click to upload file (Max 50MB)
                                          </span>
                                        </Button>
                                      ) : (
                                        <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border">
                                          <div className="flex items-center gap-3">
                                            <File className="h-5 w-5 text-stone-500" />
                                            <div>
                                              <p className="text-sm font-medium text-stone-900">
                                                {selectedFile.name}
                                              </p>
                                              <p className="text-xs text-stone-500">
                                                {formatFileSize(selectedFile.size)}
                                              </p>
                                            </div>
                                          </div>
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={removeSelectedFile}
                                          >
                                            <X className="h-4 w-4" />
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {uploadProgress > 0 && uploadProgress < 100 && (
                                    <div className="space-y-2">
                                      <div className="flex items-center justify-between text-sm">
                                        <span className="text-stone-600">Uploading...</span>
                                        <span className="text-stone-600">{uploadProgress}%</span>
                                      </div>
                                      <Progress value={uploadProgress} className="h-2" />
                                    </div>
                                  )}
                                  
                                  <Button 
                                    className="w-full bg-violet-700 hover:bg-violet-800"
                                    onClick={() => handleSubmitAssignment(assignment.assignment_id)}
                                    disabled={submitting}
                                    data-testid="confirm-submit-btn"
                                  >
                                    {submitting ? (
                                      <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        {uploadProgress > 0 ? `Uploading... ${uploadProgress}%` : "Submitting..."}
                                      </>
                                    ) : (
                                      "Submit Assignment"
                                    )}
                                  </Button>
                                </div>
                              </DialogContent>
                            </Dialog>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default CourseDetail;
